import { Rule } from 'antd/es/form';

/**
 * Common validation rule: required field with whitespace check
 * @param fieldName - The field name to display in error message
 */
export const requiredRule = (fieldName: string): Rule => ({
  required: true,
  whitespace: true,
  message: `Please enter ${fieldName}`,
});

/**
 * Common validation rule: required select field
 * @param fieldName - The field name to display in error message
 */
export const requiredSelectRule = (fieldName: string): Rule => ({
  required: true,
  message: `Please select ${fieldName}`,
});

/**
 * Common validation rule: required date field
 * @param fieldName - The field name to display in error message
 */
export const requiredDateRule = (fieldName: string): Rule => ({
  required: true,
  message: `Please enter ${fieldName}`,
});

/**
 * Factory function for creating required rules array
 * @param fieldName - The field name to display in error message
 * @param isSelect - Whether this is a select field (no whitespace check needed)
 */
export const createRequiredRules = (fieldName: string, isSelect: boolean = false): Rule[] => [
  isSelect ? requiredSelectRule(fieldName) : requiredRule(fieldName),
];

/**
 * Email validation rule
 */
export const emailRule = (): Rule => ({
  type: 'email',
  message: 'Please enter a valid email address',
});

/**
 * URL validation rule
 */
export const urlRule = (): Rule => ({
  type: 'url',
  message: 'Please enter a valid URL',
});

/**
 * Minimum length validation rule
 * @param fieldName - The field name to display in error message
 * @param min - Minimum length required
 */
export const minLengthRule = (fieldName: string, min: number): Rule => ({
  min,
  message: `${fieldName} must be at least ${min} characters`,
});

/**
 * Maximum length validation rule
 * @param fieldName - The field name to display in error message
 * @param max - Maximum length allowed
 */
export const maxLengthRule = (fieldName: string, max: number): Rule => ({
  max,
  message: `${fieldName} must not exceed ${max} characters`,
});

/**
 * Phone number validation rule (basic pattern)
 */
export const phoneRule = (): Rule => ({
  pattern: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
  message: 'Please enter a valid phone number',
});

/**
 * Number only validation rule
 * @param fieldName - The field name to display in error message
 */
export const numberOnlyRule = (fieldName: string): Rule => ({
  pattern: /^\d+$/,
  message: `${fieldName} must contain only numbers`,
});

/**
 * Alphanumeric validation rule
 * @param fieldName - The field name to display in error message
 */
export const alphanumericRule = (fieldName: string): Rule => ({
  pattern: /^[a-zA-Z0-9]+$/,
  message: `${fieldName} must contain only letters and numbers`,
});

/**
 * Password strength validation rule
 * Requires at least 8 characters with one uppercase, one lowercase, and one number
 */
export const passwordStrengthRule = (): Rule => ({
  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
  message: 'Password must be at least 8 characters with one uppercase, one lowercase, and one number',
});
